<!-- This component is used to show an upselling banner on specific pages. -->
<template>
  <div v-if="visible"
    ref="layer"
    class="upselling-page-banner-layer"
    :style="{ top: `${contentTop}px` }"
  >
    <div class="upselling-page-banner-backdrop" />
    <InfoBanner
      class="upselling-page-banner"
      :title="infoBannerDetails.title"
      :details-list="infoBannerDetails.detailsList"
      :details-paragraph="infoBannerDetails.detailsParagraph"
      :additional-details="infoBannerDetails.additionalDetails"
      :primaryCta="infoBannerDetails.primaryCta "
      :secondaryCta="{ text: 'Learn More', action: () => handleSecondaryCta(
        infoBannerDetails,
        () => isVideoPlayerModalVisible = true
      )}"
    />
    <VideoPlayerModal 
      v-model="isVideoPlayerModalVisible"
      :video-url="infoBannerDetails.secondaryCtaVideoUrl"
      :title="infoBannerDetails.title"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import InfoBanner from '@/components/InfoBanner.vue'
import { useRoute, useRouter } from 'vue-router'
import { ROUTE_UPSELLING_DETAILS, handleSecondaryCta } from '@/utils/upselling'
import VideoPlayerModal from './VideoPlayerModal.vue';
import { useStore } from 'vuex'
import { useUpselling } from '@/composables/useUpselling.ts'

const store = useStore()
const route = useRoute()
const router = useRouter()

// The banner covers only the page content: the layer starts below the last row of route tabs,
// so admins can still switch to tabs the feature doesn't gate.
const layer = ref<HTMLElement | null>(null)
const contentTop = ref(0)

const updateContentTop = () => {
  const main = layer.value?.parentElement
  if (!main) {
    return
  }
  const tabStrips = main.querySelectorAll('[data-route-tabs]')
  const lastTabStrip = tabStrips[tabStrips.length - 1]
  contentTop.value = lastTabStrip
    ? lastTabStrip.getBoundingClientRect().bottom - main.getBoundingClientRect().top
    : 0
}

let resizeObserver: ResizeObserver | null = null

const isVideoPlayerModalVisible = ref(false)

const { infoBannerDetails } = useUpselling({
  resolveDetail: () => ROUTE_UPSELLING_DETAILS.find(detail => detail.routeName?.includes(route.name as string))
})


// Check if the current route matches any of the routes that we want to upsell
const matchedDetail = computed(() =>
  ROUTE_UPSELLING_DETAILS.find(detail =>
    detail.routeName?.includes(route.name as string)
  )
)

const visible = computed(() => {
  // Visible if there is a matched detail for the current route
  // and the feature is not included in the features
  return !!matchedDetail.value && !store.state.features.includes(matchedDetail.value.feature)
})

watch(route, () => {
  if(!visible.value) {
    return
  }
  const matchedRedirectTo = (matchedDetail.value?.redirectTo ?? []).find(redirect => redirect.name === route.name)
  if (matchedRedirectTo) {
    router.push(matchedRedirectTo)
  }
}, { immediate: true })

// The page renders after the route changes, and lazy routes later still, so measure again
// whenever the content area changes size.
watch(layer, (element) => {
  resizeObserver?.disconnect()
  resizeObserver = null
  const main = element?.parentElement
  if (!main) {
    return
  }
  resizeObserver = new ResizeObserver(updateContentTop)
  resizeObserver.observe(main)
  updateContentTop()
})

watch(() => route.fullPath, () => nextTick(updateContentTop))

onMounted(() => {
  window.addEventListener('resize', updateContentTop)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
  window.removeEventListener('resize', updateContentTop)
})

</script>

<style lang="css" scoped>

.upselling-page-banner-layer {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
}

.upselling-page-banner-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(8, 10, 20, 0.5);
}

.upselling-page-banner {
  z-index: 1;
  position: relative;
  width: var(--upselling-banner-width);
}
</style>
